import {
  differenceInDays,
  endOfDay,
  format,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { RRule } from 'shared/lib/rrule';
import {
  DateFormatOptions,
  dateFormatOptionToLabelMap,
} from 'shared/types/date-format-options';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { MetaInteraction } from '../meta-interaction';

export type MetaScheduleStartDateProps = {
  schedules?: HabitSchedule[] | null;
  onChange: (schedules: HabitSchedule[]) => void;
  weekStartsOn: WeekDays;
  dateFormat: DateFormatOptions;
};

export const MetaScheduleStartDate: React.FC<MetaScheduleStartDateProps> = ({
  schedules,
  onChange,
  dateFormat,
  weekStartsOn,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();

  const activeSchedule = useActiveSchedule(schedules);
  const buttonRef = useRef<HTMLDivElement>(null);

  const label = useMemo(() => {
    const startDate = activeSchedule?.startDate;
    if (!startDate) {
      return;
    }

    if (isSameDay(startDate, today)) {
      return t('general.labels.dates.today');
    }

    if (isBefore(startDate, today)) {
      return t('general.labels.days-ago', {
        count: differenceInDays(endOfDay(today), endOfDay(startDate)),
      });
    }

    return format(startDate, 'd MMM');
  }, [activeSchedule?.startDate, t, today]);

  const occurrences = useMemo(() => {
    if (!activeSchedule?.rrule) {
      return [];
    }

    const rrule = new RRule({
      ...RRule.fromString(activeSchedule.rrule.format).origOptions,
      dtstart: startOfDay(activeSchedule.startDate),
      count: 20,
    });

    return rrule.all();
  }, [activeSchedule?.rrule, activeSchedule?.startDate]);

  const _onChange = (startDate: Timestamp) => {
    if (isBefore(startDate, today)) {
      return;
    }

    const newSchedules = structuredClone(schedules);
    const scheduleToUpdate = getActiveSchedule(newSchedules);

    if (!scheduleToUpdate) {
      return;
    }

    scheduleToUpdate.startDate = startDate;

    onChange(newSchedules!);
  };

  return (
    <>
      <Datepicker
        today={today}
        weekStartsOn={weekStartsOn}
        onChange={_onChange}
        dateFormat={dateFormat}
        selected={activeSchedule?.startDate}
        placeholderText={dateFormatOptionToLabelMap[dateFormat]}
        customInput={
          <MetaInteraction
            ref={buttonRef}
            icon={Calendar}
            placeholder={t('forms.habit.fields.start-date.label')}
            label={label}
            selected={menuOpen}
          />
        }
        highlightDates={occurrences}
        onCalendarOpen={openMenu}
        onCalendarClose={closeMenu}
      />
    </>
  );
};
